import { useState } from 'react';
import type { BillingTotals, InvoicePaymentRow, InvoiceRow } from '@dru/types';
import { INVOICE_STATUS_LABEL, PAYMENT_METHOD_LABEL } from '@dru/types';
import {
  Alert, Button, DataTable, Detail, DetailSection, Field, IconAction, InfoTip, Input, KeyFigures, RowActions, SelectField, StatusBadge, TextField, type Tone,
} from '@dru/ui';
import { Eye } from 'lucide-react';
import { api } from '../../../lib/api';
import { formatDate } from '../../../lib/format';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { Dialog } from '../../../components/Dialog';
import { holds } from '../../../layouts/ConsoleShell';
import { BillablePlantSelect } from '../../../components/BillablePlantSelect';
import { ReferenceReuseAlert, referenceFlagged, useReferenceCheck } from '../../../components/PaymentSubmissions';
import { GstinMissingWarning } from '../../../components/InvoiceDetailPage';

/** A period boundary as a date, printed like every other date in the app. */
const onDate = (value: string | null | undefined) => (value ? formatDate(value) : '—');

export const money = (paise: number, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 })
    .format(paise / 100);

/* ── Billing ────────────────────────────────────────────────── */

/**
 * What this company has been billed, has paid, and still owes — and the two
 * acts that change those numbers: raising a bill and recording a receipt.
 *
 * The totals come from the API rather than being summed here, so the figure on
 * this page is the same figure every other caller sees. The figures are money
 * only: a headcount used to sit among them, which is not a billing fact and,
 * for a platform reader, was never known.
 */
export function CompanyBilling({ tenantId }: { tenantId: string }) {
  const { permissions } = useAuth();
  const canGenerate = holds(permissions, 'invoice.create');
  const invoices = useQuery<{ items: InvoiceRow[]; totals: BillingTotals }>(
    () => api.get(`/invoices?tenantId=${tenantId}&limit=100`),
    [tenantId],
  );
  const [generating, setGenerating] = useState(false);
  const [open, setOpen] = useState<InvoiceRow | null>(null);

  const t = invoices.data?.totals;

  return (
    <div className="space-y-4">
      <KeyFigures
        label="Billing at a glance"
        figures={[
          { key: 'billed', label: 'Invoiced', value: t ? money(t.billedPaise) : '—', hint: 'All issued invoices', loading: invoices.loading },
          { key: 'paid', label: 'Received', value: t ? money(t.paidPaise) : '—', hint: 'Recorded receipts', loading: invoices.loading },
          {
            key: 'due', label: 'Outstanding',
            value: t ? money(t.outstandingPaise) : '—',
            hint: t?.outstandingPaise ? `On ${t.openCount} open invoice${t.openCount === 1 ? '' : 's'}` : 'Nothing owed',
            tone: t && t.outstandingPaise > 0 ? 'overdue' : undefined,
            loading: invoices.loading,
          },
          {
            key: 'drafts', label: 'Drafts',
            value: t ? t.draftCount : '—',
            hint: t?.draftCount ? 'Not yet issued to the company' : 'None waiting',
            tone: t?.draftCount ? 'pending' : undefined,
            loading: invoices.loading,
          },
        ]}
      />

      <DetailSection
        title={`Invoices (${invoices.data?.items.length ?? 0})`}
        action={canGenerate ? <Button size="sm" onClick={() => setGenerating(true)}>Generate invoice</Button> : undefined}
      >
        <div className="flex justify-end">
          <InfoTip label="About invoices">
            Each invoice bills one plant for the examinations certified in its period. Generating a period again is safe: an examination is never billed twice.
          </InfoTip>
        </div>

        <DataTable<InvoiceRow>
          className="mt-1"
          rows={invoices.data?.items ?? []}
          loading={invoices.loading}
          error={invoices.error}
          onRetry={invoices.reload}
          rowKey={(i) => i.id}
          onRowClick={(i) => setOpen(i)}
          empty={{
            title: 'No invoices yet',
            hint: canGenerate
              ? 'Generate one for a period once examinations have been certified.'
              : 'Invoices appear here once finance generates them.',
            action: canGenerate ? <Button onClick={() => setGenerating(true)}>Generate invoice</Button> : undefined,
          }}
          columns={[
            {
              key: 'number', header: 'Invoice', alwaysVisible: true,
              cell: (i) => (
                <div>
                  <div className="font-mono text-small font-medium text-neutral-900">{i.invoiceNumber}</div>
                  <div className="text-caption font-normal text-neutral-500">
                    {onDate(i.periodStart)} to {onDate(i.periodEnd)}
                  </div>
                </div>
              ),
            },
            {
              key: 'plant', header: 'Plant',
              cell: (i) => <span className="text-small text-neutral-700">{i.plant ? `${i.plant.code} · ${i.plant.name}` : i.billToPlantName}</span>,
            },
            {
              key: 'status', header: 'Status', mobileLabel: false,
              cell: (i) => (
                <StatusBadge tone={invoiceTone(i)}>
                  {INVOICE_STATUS_LABEL[i.status] ?? i.status}
                </StatusBadge>
              ),
            },
            {
              key: 'lines', header: 'Examinations', align: 'right', hideOnMobile: true,
              cell: (i) => <span className="text-small tabular-nums text-neutral-600">{i.lineCount ?? '—'}</span>,
            },
            {
              key: 'total', header: 'Total', align: 'right',
              cell: (i) => <span className="text-small tabular-nums font-semibold text-neutral-900">{money(i.totalPaise, i.currency)}</span>,
            },
            {
              key: 'paid', header: 'Paid', align: 'right', hideOnMobile: true,
              cell: (i) => (
                <span className="text-small tabular-nums text-neutral-600">
                  {money(i.paidPaise ?? 0, i.currency)}
                </span>
              ),
            },
            {
              key: 'due', header: 'Outstanding', align: 'right',
              cell: (i) => (
                <span className={`text-small tabular-nums ${(i.outstandingPaise ?? 0) > 0 ? 'font-medium text-overdue-600' : 'text-neutral-500'}`}>
                  {money(i.outstandingPaise ?? 0, i.currency)}
                </span>
              ),
            },
            {
              key: 'go', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
              cell: (i) => (
                <RowActions>
                  <IconAction label={`View invoice ${i.invoiceNumber}`} icon={Eye} onClick={() => setOpen(i)} />
                </RowActions>
              ),
            },
          ]}
        />
      </DetailSection>

      {generating && (
        <GenerateDialog
          tenantId={tenantId}
          onClose={() => setGenerating(false)}
          onDone={() => { setGenerating(false); invoices.reload(); }}
        />
      )}
      {open && (
        <InvoiceDialog
          tenantId={tenantId}
          invoice={open}
          onClose={() => setOpen(null)}
          onChanged={() => { invoices.reload(); setOpen(null); }}
        />
      )}
    </div>
  );
}

function invoiceTone(i: InvoiceRow): Tone {
  if (i.status === 'VOID') return 'neutral';
  if (i.status === 'DRAFT') return 'pending';
  return (i.outstandingPaise ?? 0) > 0 ? 'dueSoon' : 'covered';
}

function GenerateDialog({ tenantId, onClose, onDone }: {
  tenantId: string; onClose: () => void; onDone: () => void;
}) {
  // Last month, because that is the period somebody is almost always billing.
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  const [plantId, setPlantId] = useState('');
  const [periodStart, setStart] = useState(iso(first));
  const [periodEnd, setEnd] = useState(iso(last));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/invoices/generate?tenantId=${tenantId}`,
        { plantId, periodStart, periodEnd }, crypto.randomUUID());
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog title="Generate period invoice" onClose={onClose}>
      <BillablePlantSelect label="Plant" tenantId={tenantId} value={plantId} onChange={setPlantId} className="mt-2 w-full" />

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-caption font-medium text-neutral-700">From</span>
          <Input type="date" value={periodStart} onChange={(e) => setStart(e.target.value)} className="mt-1" />
        </label>
        <label className="block">
          <span className="text-caption font-medium text-neutral-700">To</span>
          <Input
            type="date" value={periodEnd} min={periodStart} onChange={(e) => setEnd(e.target.value)} className="mt-1"
            aria-invalid={periodEnd < periodStart || undefined}
            aria-describedby={periodEnd < periodStart ? 'period-error' : undefined}
          />
        </label>
      </div>
      {periodEnd < periodStart && (
        <p id="period-error" className="mt-1 text-caption text-danger-600">The end date is before the start date.</p>
      )}

      <p className="mt-3 text-caption font-normal text-neutral-500">
        The invoice is saved as a draft. Nothing reaches the company until it is issued, which is when the GST treatment is chosen.
        <InfoTip label="About generating an invoice" className="ml-1 align-middle">
          Generates a draft invoice for one plant's examinations certified within the selected period, using active rate card pricing.
        </InfoTip>
      </p>

      {error && <Alert>{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={!plantId || periodEnd < periodStart}>
          Generate invoice
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * One invoice: its lines, its ledger, and the acts left open to it.
 *
 * A draft can be issued or discarded. An issued invoice can only take
 * payments — it is corrected by a credit note, never edited, which is why
 * there is no edit here.
 */
function InvoiceDialog({ tenantId, invoice, onClose, onChanged }: {
  tenantId: string; invoice: InvoiceRow; onClose: () => void; onChanged: () => void;
}) {
  const { permissions } = useAuth();
  const detail = useQuery<InvoiceRow & { lines: Array<Record<string, unknown>> }>(
    () => api.get(`/invoices/${invoice.id}?tenantId=${tenantId}`), [invoice.id],
  );
  const payments = useQuery<InvoicePaymentRow[]>(
    () => api.get(`/invoices/${invoice.id}/payments?tenantId=${tenantId}`), [invoice.id],
  );
  const [paying, setPaying] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const paid = (payments.data ?? []).reduce((n, p) => n + p.amountPaise, 0);
  const outstanding = invoice.status === 'VOID' ? 0 : Math.max(0, invoice.totalPaise - paid);

  async function issue(taxTreatment: string, basisPoints: number) {
    setBusy(true); setError(null);
    try {
      await api.post(`/invoices/${invoice.id}/issue?tenantId=${tenantId}`, {
        taxTreatment,
        ...(taxTreatment === 'GST_STANDARD' ? { taxRateBasisPoints: basisPoints } : {}),
      });
      onChanged();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={`Invoice ${invoice.invoiceNumber}`} onClose={onClose} wide>
      <div className="mt-3 grid gap-3 sm:grid-cols-4">
        <Detail label="Period" value={`${onDate(invoice.periodStart)} to ${onDate(invoice.periodEnd)}`} />
        <Detail label="Total" value={money(invoice.totalPaise, invoice.currency)} />
        <Detail label="Paid" value={money(paid, invoice.currency)} />
        <Detail label="Outstanding" value={money(outstanding, invoice.currency)} />
      </div>

      {error && <Alert>{error}</Alert>}

      <section className="mt-4">
        <h3 className="text-caption font-medium uppercase tracking-wide text-neutral-500">
          Payments received ({payments.data?.length ?? 0})
        </h3>
        {payments.data?.length
          ? (
            <ul className="mt-2 divide-y divide-neutral-100 rounded border border-neutral-200">
              {payments.data.map((p) => (
                <li key={p.id} className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <span className={p.amountPaise < 0 ? 'text-danger-600' : 'text-neutral-900'}>
                      {money(p.amountPaise, invoice.currency)}
                    </span>
                    <span className="ml-2 text-xs text-neutral-500">
                      {PAYMENT_METHOD_LABEL[p.method] ?? p.method}
                      {p.reference ? ` · ${p.reference}` : ''}
                    </span>
                    {p.reversalReason && (
                      <span className="block text-caption font-normal text-neutral-500">
                        Reversal: {p.reversalReason}
                      </span>
                    )}
                  </div>
                  <span className="shrink-0 text-caption text-neutral-500">
                    {formatDate(p.receivedOn)}
                  </span>
                </li>
              ))}
            </ul>
          )
          : (
            <p className="mt-1 text-sm text-neutral-500">
              {invoice.status === 'ISSUED'
                ? 'No payment receipts recorded against this invoice yet.'
                : 'Payments can be recorded once the invoice is issued.'}
            </p>
          )}
      </section>

      {detail.data?.lines && (
        <section className="mt-4">
          <h3 className="text-caption font-medium uppercase tracking-wide text-neutral-500">
            Lines ({detail.data.lines.length})
          </h3>
          <ul className="mt-2 max-h-56 divide-y divide-neutral-100 overflow-y-auto rounded border border-neutral-200">
            {detail.data.lines.map((l) => (
              <li key={String(l.id)} className="flex items-baseline justify-between gap-3 px-3 py-1.5 text-sm">
                <span className="min-w-0 truncate text-neutral-800">{String(l.description)}</span>
                <span className="shrink-0 tabular-nums text-neutral-600">
                  {money(Number(l.amountPaise ?? 0), invoice.currency)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Close</Button>
        {invoice.status === 'DRAFT' && holds(permissions, 'invoice.approve') && (
          <Button onClick={() => setIssuing(true)} loading={busy}>Issue invoice</Button>
        )}
        {invoice.status === 'ISSUED' && outstanding > 0 && permissions.has('invoice.payment-confirm') && (
          <Button onClick={() => setPaying(true)}>Record payment</Button>
        )}
      </div>

      {issuing && (
        <IssueDialog
          billToName={detail.data?.billToName ?? invoice.billToName}
          billToGstin={detail.data ? detail.data.billToGstin : invoice.billToGstin}
          onClose={() => setIssuing(false)}
          onIssue={(treatment, bp) => { setIssuing(false); void issue(treatment, bp); }}
        />
      )}
      {paying && (
        <RecordPaymentDialog
          tenantId={tenantId}
          invoice={invoice}
          outstanding={outstanding}
          onClose={() => setPaying(false)}
          onDone={() => { setPaying(false); payments.reload(); onChanged(); }}
        />
      )}
    </Dialog>
  );
}

function IssueDialog({ billToName, billToGstin, onClose, onIssue }: {
  /** Who the invoice will be addressed to, as issuing would freeze it now. */
  billToName: string; billToGstin: string | null;
  onClose: () => void; onIssue: (treatment: string, basisPoints: number) => void;
}) {
  const [treatment, setTreatment] = useState('GST_STANDARD');
  const [rate, setRate] = useState('1800');

  return (
    <Dialog title="Issue invoice" onClose={onClose}>
      <p className="mt-1 text-sm text-neutral-600">
        Issuing makes the invoice visible to the company, gives it the next number in the tax-invoice series and fixes
        its amounts. It cannot be edited or withdrawn afterwards — a mistake is corrected with a credit note.
      </p>

      <SelectField label="GST treatment" value={treatment} onChange={setTreatment} className="mt-4">
        <option value="GST_STANDARD">Standard GST</option>
        <option value="EXEMPT">Healthcare exemption</option>
        <option value="REVERSE_CHARGE">Reverse charge</option>
      </SelectField>

      {treatment === 'GST_STANDARD' && (
        <TextField
          label="GST rate, in basis points"
          value={rate}
          onChange={(v) => setRate(v.replace(/\D/g, '').slice(0, 5))}
          inputMode="numeric"
          className="mt-3 max-w-xs"
          status={Number(rate) ? `${(Number(rate) / 100).toLocaleString('en-IN')}% — 1800 basis points is 18%.` : '1800 basis points is 18%.'}
        />
      )}

      <GstinMissingWarning className="mt-3" taxTreatment={treatment} billToGstin={billToGstin} billToName={billToName} stage="issuing" />

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => onIssue(treatment, Number(rate))} disabled={treatment === 'GST_STANDARD' && !Number(rate)}>
          Issue invoice
        </Button>
      </div>
    </Dialog>
  );
}

function RecordPaymentDialog({ tenantId, invoice, outstanding, onClose, onDone }: {
  tenantId: string; invoice: InvoiceRow; outstanding: number;
  onClose: () => void; onDone: () => void;
}) {
  // Prefilled with what is owed, because a payment in full is the ordinary
  // case and retyping the figure is how a digit goes missing.
  const [amount, setAmount] = useState((outstanding / 100).toFixed(2));
  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const paise = Math.round(Number(amount) * 100);
  const amountOk = Number.isFinite(paise) && paise > 0 && paise <= outstanding;
  // A bank transfer, UPI payment or cheque is recorded with its reference —
  // the API refuses one without (payment-reference-required). A payment with
  // a blank where the UTR belongs cannot be matched to a statement. Cash and
  // adjustments leave no bank trail and need none.
  const needsReference = method === 'BANK_TRANSFER' || method === 'UPI' || method === 'CHEQUE';
  const referenceWhat = method === 'CHEQUE' ? 'cheque number' : 'UTR or transaction reference';
  const referenceError = needsReference && reference.trim().length < 4
    ? `Enter the ${referenceWhat} (at least 4 characters).` : null;
  const valid = amountOk && !referenceError;
  const [tried, setTried] = useState(false);

  // Where else this company's account already carries the reference. Finance
  // recording directly is how one UTR came to sit on three invoices unnoticed
  // (audit of 1 Oct 2026). A warning to record past, never a refusal: one
  // transfer can pay several invoices.
  const refCheck = useReferenceCheck(invoice.id, tenantId);
  const seen = reference.trim().length >= 4 ? refCheck.current(reference) : null;
  const flagged = referenceFlagged(seen);

  async function submit() {
    setTried(true);
    if (!valid) return;
    setBusy(true); setError(null);
    try {
      // The warning must have been on screen before this click counts as
      // "record anyway"; if the look-up had not run yet, it runs now.
      if (reference.trim().length >= 4 && !seen && referenceFlagged(await refCheck.run(reference))) return;
      await api.post(`/invoices/${invoice.id}/payments?tenantId=${tenantId}`, {
        amountPaise: paise,
        receivedOn,
        method,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }, crypto.randomUUID());
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog title={`Record payment — ${invoice.invoiceNumber}`} onClose={onClose}>
      <p className="mt-1 text-sm text-neutral-600">
        {money(outstanding, invoice.currency)} is outstanding. A recorded payment cannot be edited or deleted — a mistake is put right with a reversal.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <TextField
          label="Amount received (₹)"
          required
          value={amount}
          onChange={(v) => setAmount(v.replace(/[^\d.]/g, ''))}
          inputMode="decimal"
          error={!amountOk && Number(amount) > 0 ? `Cannot be more than the ${money(outstanding, invoice.currency)} outstanding.` : null}
        />
        <TextField label="Received on" required type="date" value={receivedOn} onChange={setReceivedOn} />
        <SelectField label="Method" value={method} onChange={setMethod}>
          <option value="BANK_TRANSFER">Bank transfer</option>
          <option value="CHEQUE">Cheque</option>
          <option value="UPI">UPI</option>
          <option value="CASH">Cash</option>
          <option value="ADJUSTMENT">Adjustment</option>
        </SelectField>
        <Field
          label={method === 'CHEQUE' ? 'Cheque number' : 'Reference'}
          required={needsReference}
          error={tried ? referenceError : null}
          hint={needsReference ? `The ${referenceWhat}, as on the bank statement` : 'Optional for cash and adjustments'}
        >
          {(p) => (
            <Input
              {...p} value={reference} spellCheck={false} autoCapitalize="characters" className="font-mono"
              onChange={(e) => setReference(e.target.value)}
              onBlur={() => { void refCheck.run(reference); }}
            />
          )}
        </Field>
      </div>

      {seen && (
        <ReferenceReuseAlert
          className="mt-3" reference={reference.trim()} uses={seen.uses} elsewhere={seen.elsewhere}
          currency={invoice.currency} amountPaise={amountOk ? paise : undefined}
          invoiceHref={(u) => `/admin/invoices/${u.invoiceId}?tenantId=${tenantId}`}
        >
          One bank transfer can pay several invoices. Record it only if the credit on the statement covers every
          payment listed against this reference.
        </ReferenceReuseAlert>
      )}

      <TextField label="Notes" value={notes} onChange={setNotes} className="mt-3" />

      {error && <Alert>{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={!amountOk}>{flagged ? 'Record anyway' : 'Record payment'}</Button>
      </div>
    </Dialog>
  );
}
